import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useT, LangToggle } from "../lib/i18n";

export default function ResetPassword() {
  const t = useT();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  async function submit(event) {
    event.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError(t("auth.reset.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
    } catch {
      setError(t("auth.reset.invalid"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070B14] p-4 sm:p-8">
      <main className="w-full max-w-md grid-card space-y-6 p-6 sm:p-8">
        <div className="flex justify-end"><LangToggle /></div>
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">{t("auth.recovery.label")}</p>
          <h1 className="mt-2 text-2xl font-bold text-white">{t(success ? "auth.reset.success_title" : "auth.reset.title")}</h1>
          {!success && <p className="mt-2 text-sm leading-relaxed text-slate-300">{t("auth.reset.description")}</p>}
        </div>
        {success ? (
          <div role="status" className="space-y-4">
            <p className="border border-emerald-500/30 bg-emerald-950/30 p-4 text-sm text-emerald-100" data-testid="reset-password-success">
              {t("auth.reset.success")}
            </p>
            <Link to="/login" className="btn-primary inline-flex w-full justify-center py-2.5">{t("auth.reset.login")}</Link>
          </div>
        ) : !token ? (
          <div role="alert" className="space-y-4">
            <p className="border border-red-500/30 bg-red-950/40 p-4 text-sm text-red-200">{t("auth.reset.invalid")}</p>
            <Link to="/forgot-password" className="text-sm font-semibold text-cyan-400 hover:underline">{t("auth.reset.request_new")}</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
            <div>
              <label htmlFor="new-password" className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("auth.reset.new_password")}</label>
              <input
                id="new-password"
                type="password"
                minLength={6}
                maxLength={128}
                autoComplete="new-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="mt-1 w-full rounded-sm border border-[#1E2C42] bg-[#0B1321] px-3 py-2 text-white focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                data-testid="reset-password-new"
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("auth.reset.confirm_password")}</label>
              <input
                id="confirm-password"
                type="password"
                minLength={6}
                maxLength={128}
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="mt-1 w-full rounded-sm border border-[#1E2C42] bg-[#0B1321] px-3 py-2 text-white focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                data-testid="reset-password-confirm"
              />
            </div>
            {error && <p role="alert" className="border border-red-500/30 bg-red-950/40 px-3 py-2 text-sm text-red-200">{error}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full py-2.5" data-testid="reset-password-submit">
              {loading ? t("auth.reset.saving") : t("auth.reset.submit")}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
